import { Observable, of, pipe, throwError, lastValueFrom } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { DatePipe } from '@angular/common';
import { saveAs } from 'file-saver';
import { EventEmitter, Injectable } from '@angular/core';

import { CompetitorsListFiltersEnum } from '../modules/progress/components/competitors-list-filter-selector/competitors-list-filter-selector.service';
import { SettingsService } from './settings.service';
import {
  CompetitorInfo,
  Crew,
  EditMarkData,
  Equal_Score,
  Mark,
  RaceProgress,
  ServerResponse,
  Score,
  SensorData,
  SensorReturnData,
  TemplateImage,
  UnsuccessRegistration,
  PollResults,
  RaceProgressRegistration,
  CloudFile,
  Race,
  RaceClass,
  Registration,
  User,
} from '../models';
import { TimePeriodPipe } from '../pipes/time-period.pipe';
import { RaceDomainService } from '../domains/race.service';
import { RegistrationDomainService } from '../domains/registration.service';
import { ResultDomainService } from '../domains/result.service';
import { ProtocolDomainService } from '../domains/protocol.service';
import { AttachmentDomainService } from '../domains/attachment.service';
import { ServiceDomainService } from '../domains/service.service';
import { UserDomainService } from '../domains/user.service';
import { AuthDomainService } from '../domains/auth.service';

@Injectable({
  providedIn: 'root',
})
export class ProgressService {
  dates!: Array<Array<Array<Array<Race>>>>;
  user!: User;
  public lastaction = null;
  templateImages: TemplateImage[] = [];
  refreshTableData: EventEmitter<RaceClass['clUid']> = new EventEmitter();
  public org: boolean = false;

  constructor(
    private datePipe: DatePipe,
    private timePeriodPipePipe: TimePeriodPipe,
    private appSettings: SettingsService,
    private authDomainService: AuthDomainService,
    private raceDomainService: RaceDomainService,
    private registrationDomainService: RegistrationDomainService,
    private resultDomainService: ResultDomainService,
    private protocolDomainService: ProtocolDomainService,
    private attachmentDomainService: AttachmentDomainService,
    private serviceDomainService: ServiceDomainService,
    private userDomainService: UserDomainService
  ) {
  }

  getCompetitorInfo(cUid: string): Observable<CompetitorInfo> {
    return this.userDomainService.getCompetitorInfo(cUid);
  }

  // Обновление данных текстовых о участнике
  patchCompetitorInfo(rUid: string, cUid: string, info: string): Observable<boolean> {
    return this.userDomainService.patchCompetitorInfo(rUid, cUid, info);
  }

  //
  //
  //
  // ------------------------------------  ЗАГРУЗКА ИСХОДНЫХ ДАННЫХ - ГОНКИ, КЛАССОВ И Т.П. ----------------------------------------------------------------

  // Получает список гонок за указанный период (используется в диалоге формирования сводного протокола для выбора гонок)
  async getRaces(date: Date, dates: Array<Array<Array<Array<Race>>>>): Promise<Array<Array<Array<Array<Race>>>> | undefined> {
    const dateFrom = new Date(date.getFullYear(), date.getMonth() - 2, 1);
    const dateTo = new Date(date.getFullYear(), date.getMonth() + 3, 0);
    const dateFromStr = this.datePipe.transform(dateFrom, 'yyyy-MM-dd')!;
    const dateToStr = this.datePipe.transform(dateTo, 'yyyy-MM-dd')!;
    return lastValueFrom(
      this.raceDomainService.getList(dateFromStr, dateToStr, 'all')
        .pipe(
          map((res) => {
            res.forEach((raceDate) => {
              // if (raceDate.name.length > 38) raceDate.name = raceDate.name.substring(0,25) + '…' + raceDate.name.slice(-12);
              const date = raceDate.date.getDate();
              const month = raceDate.date.getMonth();
              const year = raceDate.date.getFullYear();
              if (!dates[year]) {
                dates[year] = [];
              }
              if (!dates[year][month]) {
                dates[year][month] = [];
              }
              if (!dates[year][month][date]) {
                dates[year][month][date] = [];
              }
              if (!dates[year][month][date].some((race) => race.rUid === raceDate.rUid)) {
                dates[year][month][date][dates[year][month][date].length] = raceDate;
              }
            });
            return dates;
          }),
        )
    );
  }

  // Получение данных гонки
  getRace(uid: Race['rUid']): Promise<Race> {
    return lastValueFrom(
      this.raceDomainService.getRace(uid)
        .pipe(
          map((res) => {
            if (this.user && this.user.admin) res.isOrg = true;
            return res;
          }),
        )
    );
  }

  // TODO описать контракт
  // Загружает в данные о гонке список классов с параметрами
  async getRaceClasses(race: Race): Promise<RaceClass[] | undefined> {
    return lastValueFrom(
      this.raceDomainService.getClasses(race.rUid, 1)
    );
  }

  // Загружает список участников гонки
  getParticipants(
    race: Race,
    cls: RaceClass,
    useCache = true,
    filter: CompetitorsListFiltersEnum = CompetitorsListFiltersEnum.NO_FILTER
  ): Promise<Array<Registration> | undefined> {
    return lastValueFrom(
      this.registrationDomainService.getRegistrationList(race.rUid, cls.clUid)
        .pipe(
          map((res: any) => {
            cls.registrations = [];
            res.forEach((item: any) => {
              const participant = new Registration(item);
              cls.registrations.push(participant);
            });
            return cls.registrations;
          }),
        )
    );
  }

  // Возвращает данные участника гонки
  getParticipantData(rUid: string, clUid: string, cUid: string, deleted: boolean): Promise<Registration> {
    return lastValueFrom(
      this.registrationDomainService.getParticipantData(rUid, clUid, cUid, deleted)
    );
  }

  //Получает список таблиц очков
  getScore(): Observable<Score[]> {
    return this.resultDomainService.getScoreTables();
  }

  //Получает варианты подсчета результатов при равенстве очков
  getEqualScores(): Observable<Equal_Score[]> {
    return this.resultDomainService.getEqualScoreVariants();
  }

  // Получает список не сумевших зарегистрироваться участников
  getUnsuccessRegistrations(rUid: string, dateFrom: string, dateTo: string): Observable<UnsuccessRegistration[]> {
    return this.registrationDomainService.getUnsuccessRegistrations(rUid, dateFrom, dateTo);
  }

  //
  //
  //
  // ------------------------------------  НАСТРОЙКА ГОНКИ - ЛОГОТИПЫ ПРОТОКОЛОВ, СТАТУС ГОНКИ -----------------------------------------------------------

  // Сохраняет логотипы, (левый, центр, правый), которые выводятся на протоколах
  saveProtocolLogos(race: Race): Promise<void> {
    return lastValueFrom(
      this.raceDomainService.saveProtocolLogos(race)
    );
  }

  // Добавляет выбранную картинку логотипа протокола как приложение гонки
  addRaceProtocolLogoAsAttachment(file: CloudFile, race: Race): Promise<void> {
    const cloudFile: CloudFile = { ...file, isPrivate: false };
    return lastValueFrom(
      this.attachmentDomainService.addAttachment('RACE', race.rUid, 'Images', cloudFile)
    ).then(() => {
      if (file.file) {
        this.templateImages.push(new TemplateImage(file.uid, file.file.name));
      }
    });
  }

  // Загружает список картинок, приложений гонки, чтобы их выбирать в качестве логотипов для протоколов
  getListImages(race: Race): Promise<void> {
    this.attachmentDomainService.getAttachmentList('RACE', race.rUid, 'Images')
      .pipe(
        tap(async (result: any) => {
          const arr = result.images;
          this.templateImages = [];

          this.templateImages.push(new TemplateImage('', this.appSettings.translate[this.appSettings.selectedLang]['blank']));
          arr.forEach((item) => {
            this.templateImages.push(new TemplateImage(item.uid, item.description));
          });
        }),
      )
      .subscribe();
    return Promise.resolve();
  }

  // Изменяет статус гонки (Регистрация открыта/закрыта, доделать статус Гонка закрыта)
  // TODO переделать на более понятную передачу, что надо сделать - открыть/закрыть регистрацию, закрыть гонку
  changeRaceState(race: Race, raceState: number): Promise<ServerResponse<number>> {
    return lastValueFrom(
      this.raceDomainService.changeRaceState(race.rUid, raceState)
        .pipe(
          map(() => ({ status: 'OK', data: raceState } as ServerResponse<number>)),
        )
    );
  }

  //
  //
  //
  // ------------------------------------  ОТМЕТКИ В РЕГИСТРАЦИИ - СТАРТОВЫЙ ВЗНОС, ДОКУМЕНТЫ УЧАСТНИКА, НОМЕРА ----------------------------------------------

  // Сохраняет отметки документов участника (принято/отказано с комментарием)
  saveDocumentData(dialogData: Registration, rUid: string): Promise<void> {
    const documents = {
      documents: dialogData.documents.map(({ valid, reason, type, uploadTime }) => {
        return { valid, reason, type, uploadTime };
      }),
      allDocumentsValid: dialogData.allDocumentsValid,
      payedValue: dialogData.startFee,
    };
    return lastValueFrom(
      this.registrationDomainService.saveDocuments(rUid, dialogData.clUid, dialogData.user.cUid, documents, Number(dialogData.startFee))

    );
  }

  // Сохраняет стартовый номер участника
  saveStartNumber(competitor: Registration, rUid: string): Promise<void> {
    return lastValueFrom(
      this.registrationDomainService.saveStartNumber(rUid, competitor.clUid, competitor.user.cUid, String(competitor.startNumber))

    );
  }

  // Сохраняет инф о регистрации участника
  saveRegInfo(competitor: Registration, rUid: string): Promise<void> {
    return lastValueFrom(
      this.registrationDomainService.saveRegInfo(rUid, competitor.clUid, competitor.user.cUid, competitor.info)
    );
  }

  // Сохраняет инф о команде участника
  saveTeamInfo(competitor: Registration, rUid: string): Promise<void> {
    return lastValueFrom(
      this.userDomainService.saveTeam(rUid, competitor.user.cUid, competitor.user.team)
    );
  }

  // Сохраняет стартовый взнос участника
  saveStartFee(competitor: Registration, rUid: string): Promise<void> {
    return lastValueFrom(
      this.registrationDomainService.saveStartFee(rUid, competitor.clUid, competitor.user.cUid, Number(competitor.startFee))
    );
  }

  // Сохраняет датчик участника
  saveChronoTag(competitor: Registration, rUid: string): Promise<void> {
    return lastValueFrom(
      this.registrationDomainService.saveChronoTag(rUid, competitor.clUid, competitor.user.cUid, competitor.chronoTag)
    );
  }

  // Сохраняет время регистрации участника
  saveRegistrationTime(rUid: string, cUid: string, time: number): Promise<void> {
    return lastValueFrom(
      this.registrationDomainService.changeRegistrationTime(rUid, cUid, time)
    );
  }

  // Сохраняет заментки орга по участнику
  saveOrgComment(competitor: Registration, rUid: string): Promise<void> {
    return lastValueFrom(
      this.registrationDomainService.saveOrgComment(rUid, competitor.clUid, competitor.user.cUid, competitor.orgComment)
    );
  }

  // Удаляет регистрацию участника
  deleteRegistration(rUid: string, clUid: string, cUid: string): Promise<void> {
    return lastValueFrom(
      this.registrationDomainService.deleteRegistrationAdmin(rUid, clUid, cUid)
    );
  }

  // Присваивает номера участникам по времени регистрации выбранным способой
  assignNumbers(rUid: string, clUid: string, variant: 'within_class' | 'across_all'): Promise<void> {
    return lastValueFrom(
      this.registrationDomainService.assignNumbers(rUid, clUid, variant)
    );
  }

  // Присваивает номера участникам по времени регистрации выбранным способой
  deleteNumbers(rUid: string, clUid: string): Promise<void> {
    return lastValueFrom(
      this.registrationDomainService.deleteNumbers(rUid, clUid)
    );
  }

  //
  //
  //
  // ------------------------------------  ПРОГРЕСС - ОНЛАЙН РЕЗУЛЬТАТЫ, ПРОТОКОЛЫ РЕЗУЛЬТАТОВ -------------------------------------------------------------

  // Найти класс (cUid) по номеру участника
  getClassByNumberCompetitor(rUid: Race['rUid'], cNumber: number): Observable<{ clUid: RaceClass['clUid']; cNumber: number }> {
    return this.registrationDomainService.getClassByNumberCompetitor(rUid, cNumber);
  }

  // Получает данные для прогресса (подробные онлайн результаты гонки)
  getRaceProgress(rUid: Race['rUid'], clUid: RaceClass['clUid'] | undefined = undefined): Promise<RaceProgress> {
    //TODO переделать на поток
    return lastValueFrom(
      this.protocolDomainService.getNewProgress(rUid, clUid)
    );
  }

  // Получает данные для формирования протокола в личном зачете
  getPersonalResult(race: Race, cls: RaceClass): Promise<any[]> {
    return lastValueFrom(
      this.protocolDomainService.getPersonalResult(race.rUid, cls.clUid)
        .pipe(
          map((res) => {
            res.forEach((per) => {
              per.name = per.name.split('$').join('\r\n');
            });
            return res;
          }),
        )
    );
  }

  // Получает данные для формирования сводного протокола по нескольким заездам/гонкам
  getOverallResult(races: Race[], cls: RaceClass, score, scores_equal): Observable<any> {
    return this.protocolDomainService.getOverallResult(races.map((race) => race.rUid), cls.name, score, scores_equal);
  }

  // Получает данные для протокола командного зачета
  getTeamResults(rUid: string, clUid: string): Promise<Crew[]> {
    return lastValueFrom(
      this.protocolDomainService.getTeamResults(rUid, clUid)
    )
      .then(response => {
        if (response) {
          return response;
        } else {
          return Promise.reject('Ошибка получения данных');
        }
      });
  }

  // Получает данные для протокола опроса при регистрации
  getPollResults(rUid: string): Promise<PollResults> {
    return lastValueFrom(
      this.protocolDomainService.getPollResults(rUid)
    )
      .then(response => {
        if (response) {
          return response;
        } else {
          return Promise.reject('Ошибка получения данных');
        }
      });
  }

  // Формирует csv файл для сохранения результатов НЕ ИСПОЛЬЗУЕТСЯ
  csv(raceProgress: RaceProgress) {
    let dl = ';';
    let header = `№ участника${dl}ФИО${dl}Город${dl}Команда${dl}Сход${dl}Причина схода${dl}Время старта${dl}Время финиша${dl}Время гонки${dl}Время гонки ms${dl}Пенализация${dl}`;
    let body = '';
    let lapsAll = 0;
    raceProgress.classes.forEach((classObj) => {
      classObj.registrations.forEach((competitor) => {
        body += `${competitor.registration.startNumber}${dl}`;
        body += `${competitor.registration.user.name}${dl}`;
        body += `${competitor.registration.user.city}${dl}`;
        body += `${competitor.registration.user.team}${dl}`;

        if (competitor.DNF) {
          body += `${competitor.DNF}${dl}`;
          body += `${competitor.DNFreason}${dl}`;
        } else {
          body += `${dl}${dl}`;
        }

        if (competitor.startTime) {
          body += `${this.datePipe.transform(competitor.startTime, 'HH:mm:ss')}${dl}`;
        } else {
          body += `${dl}`;
        }

        if (competitor.finTime) {
          body += `${this.datePipe.transform(competitor.finTime, 'HH:mm:ss')}${dl}`;
        } else {
          body += `${dl}`;
        }

        body += `${this.timePeriodPipePipe.transform(competitor.raceTime, 'HH:mm:ss')}${dl}`;
        body += `${competitor.raceTime}${dl}`;

        if (competitor.penalty) {
          body += `${competitor.penalty}${dl}`;
        } else {
          body += `${dl}`;
        }

        let laps = 0;
        competitor.laps.forEach((lap) => {
          laps += 1;
          if (lapsAll < laps) {
            header += `№ маршала${dl}Астрономическое время отметки${dl}Время от предыдущей отметки (мс)${dl}`;
            lapsAll = laps;
          }
          lap.marks.forEach((mark) => {
            body += `${mark.marshal}${dl}`;
            body += `${this.datePipe.transform(mark.time, 'HH:mm:ss')}${dl}`;
            body += `${mark.delta}${dl}`;
          });
        });

        body += `\n`;
      });
    });

    const thefile = new Blob([`${header}\n${body}`], {
      type: 'text/csv;charset=utf-8',
    });
    saveAs(thefile, `${raceProgress.race.name}.csv`);
  }

  //
  //
  //
  // ------------------------------------  КОРРЕКТИРОВКА ОТМЕТОК, СТАРТА. ПЕНАЛИЗАЦИИ, СХОДА ---------------------------------------------------------------

  // Получает список отметок маршалами участника
  getCompetitorMarks(race: Race, classRace: RaceClass, competitor: RaceProgressRegistration): Observable<EditMarkData[]> {
    return this.resultDomainService.getCompetitorMarks(race.rUid, classRace.clUid, competitor.registration.user.cUid, race.offset);
  }

  // Помечает удаленной отметку маршалом участника
  deleteMark(mark: Mark, race: Race, classRace: RaceClass, competitor: RaceProgressRegistration): Promise<void> {
    return lastValueFrom(
      this.resultDomainService.deleteMark(
        race.rUid,
        classRace.clUid,
        competitor.registration.user.cUid,
        mark.marshal,
        mark.time ? mark.time.getTime() : null
      )
    );
  }

  // Добавляет/изменяет данные отметки маршалом участника
  saveMark(marshal: number, markTime: number | null, rUid: string, clUid: string, cUid: string): Promise<void> {
    return lastValueFrom(
      this.resultDomainService.saveMark(rUid, clUid, cUid, marshal, markTime)
    );
  }

  // Корректирует время старта участника
  saveStart(time: number, rUid: string, clUid: string, cUid: string): Promise<void> {
    return lastValueFrom(
      this.resultDomainService.saveStart(rUid, clUid, cUid, time)
    );
  }

  //Сохраняет данные о пенализщации и сходе, с комментариями
  saveDnfPenalty(
    penalty: number,
    penaltyComment: string,
    dnf: number,
    dnfComment: string,
    rUid: string,
    clUid: string,
    cUid: string
  ): Promise<void> {
    return lastValueFrom(
      this.resultDomainService.saveDnfPenalty(rUid, clUid, cUid, Number(penalty), penaltyComment, Number(dnf), dnfComment)
    );
  }

  // TODO описать контракт
  // Получает список датчиков хронометража, используемых в гонке
  getChronoTagsList(race: Race): Promise<SensorData[]> {
    return lastValueFrom(
      this.resultDomainService.getChronoTagsList(race.rUid)
    );
  }

  // Отмечает датчик хронометража как возвращенный
  markChronoTagAsReturned(sensors: SensorReturnData[]): Promise<void> {
    return lastValueFrom(
      this.resultDomainService.markChronoTagsAsReturned(sensors)
    );
  }

  sendSms(phones: string[], textSms: string, rUid?: string, isPaid?: boolean): Promise<void> {
    return lastValueFrom(this.serviceDomainService.sendSms(phones, textSms, rUid || '', isPaid || false));
  }
}
